import { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const CATEGORIES_DOC_ID = 'main';

export const useScenarioCategories = () => {
  const [categories, setCategories] = useState([]);
  const hasLoaded = useRef(false);

  // Load categories from Firestore
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const ref = doc(db, 'scenarioCategories', CATEGORIES_DOC_ID);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          console.log('[useScenarioCategories] Loaded categories from Firestore:', data.categories);
          setCategories(data.categories || []);
        } else {
          console.log('[useScenarioCategories] No categories document found. Creating new document with empty categories.');
          // Initialize with empty categories array
          await setDoc(ref, { categories: [] });
          setCategories([]);
        }
      } catch (e) {
        console.error('Failed to load categories:', e);
        // Fallback to empty array if there's an error
        setCategories([]);
      } finally {
        hasLoaded.current = true;
      }
    };
    fetchCategories();
  }, []);

  // Persist categories to Firestore whenever they change (but not on initial load)
  useEffect(() => {
    const saveCategories = async () => {
      try {
        console.log('[useScenarioCategories] Saving categories to Firestore:', categories);
        const ref = doc(db, 'scenarioCategories', CATEGORIES_DOC_ID);
        await setDoc(ref, { categories });
        console.log('[useScenarioCategories] Categories saved successfully.');
      } catch (e) {
        console.error('[useScenarioCategories] Failed to save categories:', e);
      }
    };
    if (Array.isArray(categories) && hasLoaded.current) {
      saveCategories();
    }
  }, [categories]);

  const addCategory = (name, color) => {
    const newCategories = [...categories, { 
      id: Date.now().toString(), 
      name, 
      color, 
      sheets: [], 
      expanded: true 
    }];
    setCategories(newCategories);
  };

  const updateCategory = (id, name, color) => {
    const newCategories = categories.map(c => 
      c.id === id ? { ...c, name, color } : c
    );
    setCategories(newCategories);
  };

  const deleteCategory = (id) => {
    setCategories(categories.filter(c => c.id !== id));
  };

  const toggleCategoryExpansion = (id) => {
    setCategories(cs => cs.map(c => 
      c.id === id ? { ...c, expanded: !c.expanded } : c
    ));
  };

  return {
    categories,
    setCategories,
    addCategory,
    updateCategory,
    deleteCategory,
    toggleCategoryExpansion
  };
};
